import { useState } from 'react';

/**
 * Shipping-related state for the QuoteCalculator wizard.
 *
 * Bundles the four pieces of state that Page 3 (Shipping) owns:
 *   - shippingAddress: the multi-field controlled form object
 *   - country / countryUserSet: postal-code auto-detection respects
 *     the userSet flag so a manual dropdown change isn't clobbered
 *     by subsequent keystrokes.
 *   - currency / currencyUserSet: same flag pattern — Country
 *     auto-sync (`useCountryCurrencyAutoSync`) only writes when the
 *     user hasn't manually picked.
 *
 * Also exposes `isShippingAddressComplete()` so the Place-Order CTA
 * can disable until every required field is filled in.
 *
 * Args:
 *   user — the AuthContext user object (used for sane defaults). May
 *          be null on first render before AuthContext hydrates; we
 *          fall back to "United States" + "USD" in that case.
 */
export function useShippingAddressState(user) {
  const [shippingAddress, setShippingAddress] = useState({
    companyName: '',
    contactName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateProvince: '',
    postalCode: '',
  });

  // Seed from the user's saved country if available — so a Canadian
  // customer doesn't land on Page 3 with the dropdown stuck on USA.
  const [country, setCountry] = useState(() => user?.country || 'United States');
  // True once the customer hand-picks a country from the Page 3 dropdown.
  // Prevents postal-code-based auto-detection from clobbering their
  // explicit choice on subsequent keystrokes.
  const [countryUserSet, setCountryUserSet] = useState(false);

  // Currency auto-detects from the user's saved country (CAD for
  // Canada, USD elsewhere). userSet flag prevents auto-sync from
  // clobbering a manual dropdown change.
  const initialCurrency = user?.country === 'Canada' ? 'CAD' : 'USD';
  const [currency, setCurrency] = useState(initialCurrency);
  const [currencyUserSet, setCurrencyUserSet] = useState(false);

  const isShippingAddressComplete = () =>
    shippingAddress.contactName.trim() !== '' &&
    shippingAddress.addressLine1.trim() !== '' &&
    shippingAddress.city.trim() !== '' &&
    shippingAddress.stateProvince.trim() !== '' &&
    shippingAddress.postalCode.trim() !== '';

  return {
    shippingAddress,
    setShippingAddress,
    country,
    setCountry,
    countryUserSet,
    setCountryUserSet,
    currency,
    setCurrency,
    currencyUserSet,
    setCurrencyUserSet,
    isShippingAddressComplete,
  };
}
